import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, MapPin, Phone, Mail, AlertCircle, ArrowRight } from 'lucide-react';
import { supabase } from '../services/supabase';
import type { LandingPageData } from '../types';

interface PublicLandingPageProps {
  slug: string;
  uprn: string | null;
}

export default function PublicLandingPage({ slug, uprn }: PublicLandingPageProps) {
  const [data, setData] = useState<LandingPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [bestTime, setBestTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Track the QR scan in the background.
      if (uprn && slug !== 'preview') {
        Promise.resolve(
          supabase.rpc('doorsteps_track_qr_scan', { p_slug: slug, p_uprn: uprn })
        ).catch(() => {});
      }
      try {
        const { data, error } = await supabase.rpc('doorsteps_get_landing_page', {
          p_slug: slug,
          p_uprn: uprn,
        });
        if (cancelled) return;
        if (error || !data || (data as any).error === 'not_found') {
          setNotFound(true);
        } else {
          setData(data as LandingPageData);
        }
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, uprn]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const composed = [bestTime ? `Best time to call: ${bestTime}` : '', message].filter(Boolean).join('\n');
      const { data: result, error: err } = await supabase.rpc('doorsteps_submit_valuation', {
        p_slug: slug,
        p_uprn: uprn ?? '',
        p_name: name,
        p_email: email,
        p_phone: phone,
        p_message: composed || null,
      });
      if (err || !(result as any)?.success) {
        throw new Error((result as any)?.error || err?.message || 'Submission failed');
      }
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-600" size={32} />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <AlertCircle size={40} className="mx-auto text-slate-300 mb-3" />
          <h1 className="text-xl font-semibold text-slate-900 mb-1">Campaign not found</h1>
          <p className="text-sm text-slate-500">The link you followed may have expired or been mistyped.</p>
        </div>
      </div>
    );
  }

  // Preview mode (slug='preview') uses placeholder content.
  const isPreview = slug === 'preview';
  const settings = data?.agent_settings ?? null;
  const property = data?.property ?? null;
  const comp = data?.comparable_sale ?? null;
  const primary = settings?.primary_color || '#0f172a';
  const agencyName = settings?.agency_name || (isPreview ? 'Smith & Co Estate Agents' : 'Your Agency');
  const agentName = settings?.agent_full_name || (isPreview ? 'Sarah Smith' : '');
  const street = property?.address_line_1?.replace(/^\d+\s*/, '').split(',')[0] || (isPreview ? 'Acacia Avenue' : 'your street');

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Brand band */}
      <header className="px-6 py-4 bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          {settings?.logo_url ? (
            <img src={settings.logo_url} alt={agencyName} className="h-10 w-auto" />
          ) : (
            <div className="text-lg font-semibold" style={{ color: primary }}>{agencyName}</div>
          )}
          <div className="text-xs text-slate-500 text-right">
            {settings?.contact_phone && (
              <div className="flex items-center gap-1 justify-end"><Phone size={11} /> {settings.contact_phone}</div>
            )}
            {settings?.contact_email && (
              <div className="flex items-center gap-1 justify-end mt-0.5"><Mail size={11} /> {settings.contact_email}</div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-3 leading-tight" style={{ color: primary }}>
          What's your home on {street} worth today?
        </h1>
        <p className="text-slate-600 mb-8 max-w-xl">
          {agencyName} keeps a close eye on prices in your area. Get a no-obligation valuation from {agentName || 'one of our team'} — usually within 24 hours.
        </p>

        {property && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 flex items-start gap-3">
            <MapPin size={18} className="text-emerald-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-slate-900">{property.address_line_1}</div>
              <div className="text-sm text-slate-500">
                {[property.address_line_2, property.town, property.postcode].filter(Boolean).join(', ')}
              </div>
            </div>
          </div>
        )}

        {comp && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mb-6">
            <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-1">Recent sale nearby</div>
            <div className="text-emerald-900">
              A property on <strong>{comp.postcode}</strong> recently sold for{' '}
              <strong>£{comp.price.toLocaleString('en-GB')}</strong>.
            </div>
          </div>
        )}

        {submitted ? (
          <div className="bg-white border border-emerald-200 rounded-2xl p-8 text-center">
            <CheckCircle2 size={48} className="text-emerald-500 mx-auto mb-3" />
            <h2 className="text-xl font-semibold text-slate-900 mb-1">Thanks — we'll be in touch.</h2>
            <p className="text-sm text-slate-500">
              {agentName || 'A member of our team'} will contact you shortly to arrange your valuation.
            </p>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 shadow-sm"
          >
            <h2 className="font-semibold text-slate-900 mb-2">Request a valuation</h2>

            <div className="grid md:grid-cols-2 gap-3">
              <input
                required
                placeholder="Your name *"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-400"
              />
              <input
                required
                type="tel"
                placeholder="Phone *"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-400"
              />
            </div>

            <input
              required
              type="email"
              placeholder="Email *"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-400"
            />

            <select
              value={bestTime}
              onChange={(e) => setBestTime(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:border-emerald-400"
            >
              <option value="">Best time to call (optional)</option>
              <option value="Morning (9am–12pm)">Morning (9am–12pm)</option>
              <option value="Afternoon (12pm–5pm)">Afternoon (12pm–5pm)</option>
              <option value="Evening (5pm–8pm)">Evening (5pm–8pm)</option>
            </select>

            <textarea
              rows={3}
              placeholder="Anything we should know? (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-400 resize-none"
            />

            {error && (
              <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-700">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || isPreview}
              className="w-full flex items-center justify-center gap-2 py-3 text-white rounded-lg font-semibold disabled:opacity-60"
              style={{ background: primary }}
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
              {isPreview ? 'Preview mode — submission disabled' : 'Request my valuation'}
            </button>

            <p className="text-xs text-slate-400 text-center">
              Your details will only be shared with {agencyName}. See our{' '}
              <a href="https://rehub.studio/privacy" className="underline">privacy policy</a>.
            </p>
          </form>
        )}
      </main>

      <footer className="text-center text-xs text-slate-400 py-6">
        Powered by Doorsteps · ReHub Studio
      </footer>
    </div>
  );
}
